import React from 'react';
import styled from 'styled-components';

import { screenFilterSummary } from 'containers/Intelligence/filters/screenFilterSignature';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { useOrbitContext } from '../OrbitContext';
import { describeOrbitContext, describeOrbitFilterChip } from '../contextLabel';
import OrbitGlyphText from '../glyphIcons';
import { TOrbit } from '../i18n/TModules';
import { getOrbitScreenLabelKey } from '../screenLabels';
import { OrbitChip, OrbitChipRow, orbitTokens, orbitType } from '../styles';
import OrbitCardChips from './OrbitCardChips';
import OrbitChipIcon from './OrbitChipIcon';

/*
  TODO(AF-10): the drawn Region chip (`5032:12029`, `4952:5404`) has no source. Neither screen has a
  region filter — `IntelligenceFilterId` (`containers/Intelligence/types.ts`) is an exhaustive union
  of `dateRange`, `lifecycle`, `purchaseBehaviour`, `customerValue`, `engagement`,
  `productRelationship`, `demographics` and `consentStatus`, and `Customer360FilterId` of
  `dateRange`, `activityType` and `productHolding` — so a chip reading "All" would assert something
  nothing has checked. It stays out until a region filter exists. The data-source chips
  (`5032:9722`) need a source descriptor the turn payload has no field for.
*/

/*
  Sticks to the top of the scrolling panel body. The negative margins let the panel-coloured
  background span the body's full 20px gutter so the transcript cannot show through at the sides,
  and the matching padding keeps the block's content on the same baseline stuck or unstuck.

  `top` is the body's own 18px padding-top negated, not 0: a sticky box is clamped against the
  scrollport's CONTENT edge, so top: 0 pushed the block back down by that padding and left an 18px
  strip of the body above it for the scrolled transcript to show through.
*/
const Block = styled.div`
  position: -webkit-sticky;
  position: sticky;
  top: -18px;
  z-index: 1;
  margin: -18px -20px 0;
  padding: 18px 20px 12px;
  background: ${orbitTokens.surface};
`;

// div.greet-card `5032:11967`: 447 x 84 at padding 18 20, stroke #212D4B, radius 12. The 16px below
// it is the frame's own gap from the card's 234 bottom to the chip row's 250 top.
const GreetCard = styled.div`
  min-height: 84px;
  margin-bottom: 16px;
  padding: 18px 20px;
  border: 1px solid ${orbitTokens.border};
  border-radius: 12px;
`;

const GreetTitle = styled.h2`
  margin: 0;
  color: ${orbitTokens.text};
  ${orbitType.contextTitle}
  font-weight: 700;
`;

/*
  The pinned note, under the chips. Same ink and size as the greet body, which is the panel's own
  secondary line; it is a statement about the row above it, not a control.
*/
const PinnedNote = styled.p`
  margin: 8px 0 0;
  color: ${orbitTokens.textSecondary};
  ${orbitType.helping}
  font-weight: 400;
`;

const GreetBody = styled.p`
  margin: 6px 0 0;
  color: ${orbitTokens.textSecondary};
  ${orbitType.contextSubtitle}
  font-weight: 400;
`;

/**
 * Panel context section: the greet card (`5032:11967`) and the chip row (`5032:12025`) as ONE
 * sticky block at the top of the scrolling body, so the conversation runs under it. The frame draws
 * the pair as two blocks and repeats no heading, so "Context Detected" appears exactly once here.
 *
 * Renders nothing when the route carries no screen Orbit recognises: the headline is a claim about
 * the user's context, and there is nothing to back it with when no chip has a value.
 */
const OrbitContextBlock: React.FC = () => {
  const { t } = useMultiLanguageTranslation();
  const { entryContext, entryFilters, sessionContext, sessionChips, sessionCards, contextPinned } =
    useOrbitContext();

  /*
    Keep Current Investigation freezes what `ask()` sends (`OrbitProvider` askContext), so from that
    point the row has to name the PINNED context: drawn live, it named the screen the reader had
    scrolled to while the answers on screen were about the one the conversation started on
    (PLA-9563 §1c). Unpinned, every question carries the live screen and the live row is correct.
  */
  const pinnedContext =
    contextPinned && sessionContext !== null && sessionContext.screen !== undefined
      ? sessionContext
      : null;
  const shownContext = pinnedContext ?? entryContext;
  const shownCards = (pinnedContext === null ? entryFilters?.cards : sessionCards) ?? [];
  const shownChips = (pinnedContext === null ? entryFilters?.chips : sessionChips) ?? [];
  const screenLabelKey = getOrbitScreenLabelKey(shownContext.screen);

  const liveLabel =
    getOrbitScreenLabelKey(entryContext.screen) === undefined
      ? null
      : describeOrbitContext(t, entryContext);
  // Compared through the shared formatter so the two sides cannot be written differently.
  const liveDiffers =
    pinnedContext !== null &&
    liveLabel !== null &&
    (liveLabel !== describeOrbitContext(t, shownContext) ||
      screenFilterSummary(entryFilters) !==
        screenFilterSummary({ key: '', chips: shownChips, cards: shownCards }));

  if (screenLabelKey === undefined) {
    return null;
  }

  return (
    <Block data-testid="orbit-context-block">
      <GreetCard data-testid="orbit-context-greet">
        <GreetTitle>{t(`${TOrbit}.home.greetTitle`)}</GreetTitle>
        <GreetBody>{t(`${TOrbit}.home.greetBody`)}</GreetBody>
      </GreetCard>
      <OrbitChipRow data-testid="orbit-context-chips">
        {/* The customer is named, not just the screen: two Customer 360 records drew the same chip
            and were indistinguishable in the panel (PLA-8844 D7). */}
        <OrbitChip data-testid="orbit-context-chip-screen">
          <OrbitChipIcon kind="screen" />
          <OrbitGlyphText
            text={t(`${TOrbit}.context.screen`, { screen: describeOrbitContext(t, shownContext) })}
          />
        </OrbitChip>
        {/* Every card the page has on screen, between the screen and its filters: they are places
            inside the screen, and they are what the question will be read against. Redrawn as the
            reader scrolls (PLA-8844, user 2026-09-22). */}
        <OrbitCardChips cards={shownCards} testIdPrefix="orbit-context" />
        {/* One chip per filter the screen is actually applying, in the order its own filter bar
            lists them. Derived from the live signature rather than captured when the panel opened,
            so a filter changed with the panel open redraws the row (PLA-8844). */}
        {shownChips.map(chip => (
          <OrbitChip key={chip.id} data-testid={`orbit-context-chip-${chip.id}`}>
            <OrbitChipIcon kind={chip.id} />
            <OrbitGlyphText text={describeOrbitFilterChip(t, chip)} />
          </OrbitChip>
        ))}
      </OrbitChipRow>
      {pinnedContext !== null ? (
        <PinnedNote data-testid="orbit-context-pinned-hint">
          {liveDiffers
            ? t(`${TOrbit}.context.pinnedNote`, { screen: liveLabel })
            : t(`${TOrbit}.context.pinnedSame`)}
        </PinnedNote>
      ) : null}
    </Block>
  );
};

export default OrbitContextBlock;
